<script lang="ts">
  import { router, Link } from '@inertiajs/svelte';
  import momentiumLogo from '../assets/momentium-logo.png';
  import type { Scale, View, Range, ViewConfig, DisplayFilter, NavbarConfig } from '../lib/types';
  import { DEFAULT_NAVBAR_VIEWS } from '../lib/types';
  import { getMonthDisplayName, getWeekDisplayRangeWithYear } from '../lib/utils';
  import {
    getRangeOptionsForView,
    timelineUrlForDisplay,
    timelineUrlForRange,
    timelineUrlForScale,
    timelineUrlForView,
    type TimelineState,
  } from '../lib/timelineUrl';
  import { navbarConfig } from '../lib/stores/favorites';
  import ViewTooltip from './ViewTooltip.svelte';
  import NavTooltip from './NavTooltip.svelte';
  import NavDropdown from './NavDropdown.svelte';
  import FavoritesDropdown from './FavoritesDropdown.svelte';
  import FavoriteStar from './FavoriteStar.svelte';
  import ProfileDropdown from './ProfileDropdown.svelte';
  import { isWorkQueueOpen, toggleWorkQueue } from '../lib/stores/ui';
  import { nowDensity, toggleNowVisibility } from '../lib/stores/now';
  import NowGlyph from './NowGlyph.svelte';

  interface Props {
    currentMonth: string;
    currentWeek: string;
    currentYear: number;
    scale: Scale;
    view: View;
    range: Range;
    display: DisplayFilter | null;
    viewConfig: ViewConfig;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
  }

  let {
    currentMonth,
    currentWeek,
    currentYear,
    scale,
    view,
    range,
    display,
    viewConfig,
    onPrevious,
    onNext,
    onToday,
  }: Props = $props();

  // === Navbar Config ===
  let currentNavbarConfig = $derived($navbarConfig);

  // Tooltip state: which view button is being hovered
  let tooltipView: View | null = $state(null);
  let tooltipTimer: ReturnType<typeof setTimeout> | null = null;

  function showTooltip(v: View) {
    if (tooltipTimer) clearTimeout(tooltipTimer);
    tooltipTimer = setTimeout(() => {
      tooltipView = v;
    }, 400);
  }

  function hideTooltip() {
    if (tooltipTimer) clearTimeout(tooltipTimer);
    tooltipTimer = null;
    tooltipView = null;
  }

  // Generic nav tooltip state (for nav tabs, dropdowns, favorites, etc.)
  let navTooltipKey: string | null = $state(null);
  let navTooltipTimer: ReturnType<typeof setTimeout> | null = null;

  function showNavTooltip(key: string) {
    if (navTooltipTimer) clearTimeout(navTooltipTimer);
    navTooltipTimer = setTimeout(() => {
      navTooltipKey = key;
    }, 400);
  }

  function hideNavTooltip() {
    if (navTooltipTimer) clearTimeout(navTooltipTimer);
    navTooltipTimer = null;
    navTooltipKey = null;
  }

  // Control dropdown descriptions
  const controlDescriptions: Record<string, { title: string; description: string }> = {
    scale: {
      title: 'Scale',
      description:
        'Switch between week, month, and year time scales. Changes how much time each view covers.',
    },
    range: {
      title: 'Range',
      description:
        'Adjust how many periods are shown at once. See more or fewer weeks/months depending on your planning needs.',
    },
    display: {
      title: 'Display Filter',
      description: 'Choose what to show — projects only, events only, or both together.',
    },
    'favorite-star': {
      title: 'Save Favorite',
      description:
        'Bookmark the current view, scale, and range configuration for quick access later.',
    },
    workQueue: {
      title: 'Work Queue',
      description:
        'Open your project backlog or priority tasks without leaving the current view. Press W to toggle.',
    },
    now: {
      title: 'Now',
      description:
        'Show or hide your daily commitment slots. Press N to toggle. Hidden by default for new visitors.',
    },
  };

  // === View Configuration ===

  // View labels come from the backend VIEW_REGISTRY via viewConfig
  let viewLabels = $derived(viewConfig.viewLabels);

  const scaleLabels: Record<Scale, string> = {
    month: 'Month',
    week: 'Week',
    year: 'Year',
  };

  const displayLabels: Record<DisplayFilter, string> = {
    both: 'Both',
    projects: 'Projects',
    events: 'Events',
  };

  const rangeLabels: Record<string, string> = {
    // Month ranges
    '1-month': '1 Month',
    quarter: 'Quarter',
    '3-months': '3 months',
    '6-months': '6 months',
    year: 'Year',
    // Week ranges
    '1-week': '1 Week',
    month: 'Month',
    '5': '5 weeks',
    '7': '7 weeks',
    // Year ranges
    full: 'Full Year',
    half: 'Half Year',
  };

  // === Computed State ===

  // Navbar views: user-selected views shown as top-level buttons (1-3)
  let userNavbarViews = $derived(
    (currentNavbarConfig.navbar_views || DEFAULT_NAVBAR_VIEWS) as View[],
  );
  let primaryViews = $derived(
    userNavbarViews.filter(
      (v) =>
        (viewConfig.primaryViews as View[]).includes(v) ||
        (viewConfig.secondaryViews as View[]).includes(v),
    ),
  );
  // All remaining views go to "More" dropdown
  let allKnownViews = $derived([
    ...viewConfig.primaryViews,
    ...viewConfig.secondaryViews,
  ] as View[]);
  let secondaryViews = $derived(allKnownViews.filter((v) => !userNavbarViews.includes(v)));
  let validViewsForScale = $derived(viewConfig.viewsByScale[scale] || []);
  // Get valid ranges for current view+scale (considers view-specific overrides)
  // The "days" view always uses week-style ranges regardless of scale
  let validRanges = $derived(getRangeOptionsForView(view, scale, viewConfig));
  let hasRanges = $derived(validRanges.length > 0);
  let viewSupportsRange = $derived(viewConfig.viewsWithRangeSupport?.includes(view) ?? true);
  let validDisplayOptions = $derived(
    (viewConfig.validDisplayOptions[view] || []) as DisplayFilter[],
  );
  let hasDisplayOptions = $derived(validDisplayOptions.length > 0);

  // Check if a view is valid for the current scale
  function isViewValidForCurrentScale(v: View): boolean {
    return validViewsForScale.includes(v);
  }

  // Check if a scale is valid for the current view
  function isScaleValidForCurrentView(s: Scale): boolean {
    const viewsForScale = viewConfig.viewsByScale[s] || [];
    return viewsForScale.includes(view);
  }

  // Get the first valid scale for a view (used for "More" dropdown hints)
  function getScaleForView(v: View): Scale | null {
    for (const s of ['week', 'month', 'year'] as Scale[]) {
      if (viewConfig.viewsByScale[s]?.includes(v)) {
        return s;
      }
    }
    return null;
  }

  // === Navigation Handlers ===
  // URLs come from lib/timelineUrl (shared with the mobile Timeline header).

  let timelineState = $derived<TimelineState>({
    scale,
    view,
    range,
    display,
    currentMonth,
    currentWeek,
    currentYear,
    viewConfig,
  });

  function go(url: string | null) {
    if (url) router.get(url, {}, { preserveScroll: true });
  }

  function setView(newView: View) {
    go(timelineUrlForView(timelineState, newView));
  }

  function setScale(newScale: Scale) {
    go(timelineUrlForScale(timelineState, newScale));
  }

  function setRange(newRange: string) {
    go(timelineUrlForRange(timelineState, newRange));
  }

  function setDisplay(newDisplay: DisplayFilter) {
    go(timelineUrlForDisplay(timelineState, newDisplay));
  }

  // === Display Helpers ===

  function getTimeDisplayLabel(): string {
    switch (scale) {
      case 'year':
        return String(currentYear);
      case 'week':
        return getWeekDisplayRangeWithYear(currentWeek);
      default:
        return getMonthDisplayName(currentMonth);
    }
  }

  function selectSecondaryView(v: View) {
    setView(v);
  }
</script>

<header class="header">
  <!-- Left: Logo, Time Navigation -->
  <div class="header-left">
    <Link href="/" class="logo"
      ><img src={momentiumLogo} alt="Momentium" class="logo-icon" /><span class="logo-text"
        >Momentium</span
      ></Link
    >

    <NavDropdown activePage="timeline" />

    <!-- Time Navigation -->
    <div class="time-nav">
      <button class="nav-btn" onclick={onPrevious} title="Previous">←</button>
      <button class="time-display" onclick={onToday} title="Go to today">
        {getTimeDisplayLabel()}
      </button>
      <button class="nav-btn" onclick={onNext} title="Next">→</button>
    </div>

    <button class="today-btn" onclick={onToday}>Today</button>
  </div>

  <!-- Right: View Controls -->
  <div class="header-right">
    <!-- Favorites Dropdown -->
    <FavoritesDropdown
      currentView={view}
      currentScale={scale}
      currentRange={range}
      currentDisplay={display}
      {viewConfig}
    />

    <!-- Primary View Buttons -->
    <div class="view-buttons">
      {#each primaryViews as v}
        {@const isActive = view === v}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="view-btn-wrapper"
          onmouseenter={() => showTooltip(v)}
          onmouseleave={hideTooltip}
        >
          <button class="view-btn" class:active={isActive} onclick={() => setView(v)}>
            <span class="view-icon">{viewLabels[v].icon}</span>
            <span class="view-label">{viewLabels[v].label}</span>
          </button>
          <ViewTooltip view={v} visible={tooltipView === v} />
        </div>
      {/each}

      <!-- Show active secondary view as a button when selected -->
      {#if secondaryViews.includes(view)}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="view-btn-wrapper"
          onmouseenter={() => showTooltip(view)}
          onmouseleave={hideTooltip}
        >
          <button class="view-btn active promoted" onclick={() => {}}>
            <span class="view-icon">{viewLabels[view].icon}</span>
            <span class="view-label">{viewLabels[view].label}</span>
          </button>
          <ViewTooltip {view} visible={tooltipView === view} />
        </div>
      {/if}

      <!-- More Dropdown -->
      <div class="more-dropdown-container hover-dropdown">
        <button class="view-btn more-btn" title="More views">
          <span class="view-icon">⋯</span>
          <span class="view-label">More</span>
        </button>

        <div class="more-dropdown">
          {#each secondaryViews as v}
            {@const isActive = view === v}
            {@const isValid = isViewValidForCurrentScale(v)}
            {@const targetScale = getScaleForView(v)}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="dropdown-item-wrapper"
              onmouseenter={() => showTooltip(v)}
              onmouseleave={hideTooltip}
            >
              <button
                class="dropdown-item"
                class:active={isActive}
                onclick={() => selectSecondaryView(v)}
              >
                <span class="dropdown-icon">{viewLabels[v].icon}</span>
                <span class="dropdown-label">{viewLabels[v].label}</span>
                {#if !isValid && targetScale}
                  <span class="dropdown-hint">→ {scaleLabels[targetScale]}</span>
                {/if}
              </button>
              <ViewTooltip view={v} visible={tooltipView === v} position="left" />
            </div>
          {/each}
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="divider"></div>

    <!-- Scale Dropdown -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="control-dropdown hover-dropdown" onmouseleave={hideNavTooltip}>
      <button
        class="control-trigger"
        title="Scale"
        onmouseenter={() => showNavTooltip('scale')}
        onmouseleave={hideNavTooltip}
      >
        <svg
          class="control-trigger-icon"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="5.5" cy="5.5" r="3.5" /><line x1="8" y1="8" x2="13" y2="13" />
        </svg>
        <span class="control-trigger-label">{scaleLabels[scale]}</span>
        <svg class="control-chevron" viewBox="0 0 10 6" fill="currentColor"
          ><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" /></svg
        >
      </button>
      <div class="control-dropdown-menu">
        {#each Object.entries(scaleLabels) as [s, label]}
          {@const isValid = isScaleValidForCurrentView(s as Scale)}
          <button
            class="control-dropdown-item"
            class:active={scale === s}
            disabled={!isValid}
            onclick={() => setScale(s as Scale)}
          >
            {label}{!isValid ? ' (N/A)' : ''}
          </button>
        {/each}
      </div>
      <NavTooltip
        title={controlDescriptions.scale.title}
        description={controlDescriptions.scale.description}
        visible={navTooltipKey === 'scale'}
        position="below-left"
      />
    </div>

    <!-- Range Dropdown (if applicable) -->
    {#if hasRanges}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="control-dropdown hover-dropdown"
        class:disabled-dropdown={!viewSupportsRange}
        onmouseleave={hideNavTooltip}
      >
        <button
          class="control-trigger"
          title={!viewSupportsRange ? 'This view always shows full range' : 'Range'}
          disabled={!viewSupportsRange}
          onmouseenter={() => showNavTooltip('range')}
          onmouseleave={hideNavTooltip}
        >
          <svg
            class="control-trigger-icon"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <line x1="2" y1="8" x2="14" y2="8" /><line x1="4" y1="5" x2="4" y2="11" /><line
              x1="12"
              y1="5"
              x2="12"
              y2="11"
            />
          </svg>
          <span class="control-trigger-label">
            {#if viewSupportsRange}
              {(range && rangeLabels[range]) || range || 'Range'}
            {:else}
              N/A
            {/if}
          </span>
          <svg class="control-chevron" viewBox="0 0 10 6" fill="currentColor"
            ><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" /></svg
          >
        </button>
        {#if viewSupportsRange}
          <div class="control-dropdown-menu">
            {#each validRanges as r}
              <button
                class="control-dropdown-item"
                class:active={range === r}
                onclick={() => setRange(r)}
              >
                {rangeLabels[r] || r}
              </button>
            {/each}
          </div>
        {/if}
        <NavTooltip
          title={controlDescriptions.range.title}
          description={controlDescriptions.range.description}
          visible={navTooltipKey === 'range'}
          position="below-left"
        />
      </div>
    {/if}

    <!-- Display Filter Dropdown (if applicable) -->
    {#if hasDisplayOptions}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div class="control-dropdown hover-dropdown" onmouseleave={hideNavTooltip}>
        <button
          class="control-trigger"
          title="Display filter"
          onmouseenter={() => showNavTooltip('display')}
          onmouseleave={hideNavTooltip}
        >
          <svg
            class="control-trigger-icon"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path d="M2 4h12M4 8h8M6 12h4" />
          </svg>
          <span class="control-trigger-label">{display ? displayLabels[display] : 'Display'}</span>
          <svg class="control-chevron" viewBox="0 0 10 6" fill="currentColor"
            ><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" /></svg
          >
        </button>
        <div class="control-dropdown-menu">
          {#each validDisplayOptions as d}
            <button
              class="control-dropdown-item"
              class:active={display === d}
              onclick={() => setDisplay(d)}
            >
              {displayLabels[d]}
            </button>
          {/each}
        </div>
        <NavTooltip
          title={controlDescriptions.display.title}
          description={controlDescriptions.display.description}
          visible={navTooltipKey === 'display'}
          position="below-left"
        />
      </div>
    {/if}

    <!-- Favorite Star (save/unsave current view config) -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="nav-tooltip-wrapper"
      onmouseenter={() => showNavTooltip('favorite-star')}
      onmouseleave={hideNavTooltip}
    >
      <FavoriteStar {view} {scale} {range} {display} />
      <NavTooltip
        title={controlDescriptions['favorite-star'].title}
        description={controlDescriptions['favorite-star'].description}
        visible={navTooltipKey === 'favorite-star'}
        position="below-left"
      />
    </div>

    <!-- Now: daily commitment slots -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="nav-tooltip-wrapper"
      onmouseenter={() => showNavTooltip('now')}
      onmouseleave={hideNavTooltip}
    >
      <button
        type="button"
        class="priority-toggle-btn"
        class:active={$nowDensity !== 'hidden'}
        onclick={toggleNowVisibility}
        title="Toggle Now (N)"
        aria-label="Toggle Now"
      >
        <NowGlyph />
      </button>
      <NavTooltip
        title={controlDescriptions.now.title}
        description={controlDescriptions.now.description}
        visible={navTooltipKey === 'now'}
        position="below-left"
      />
    </div>

    <!-- Work queue: a compact entry point for backlog and priority tasks -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="nav-tooltip-wrapper"
      onmouseenter={() => showNavTooltip('workQueue')}
      onmouseleave={hideNavTooltip}
    >
      <button
        type="button"
        class="priority-toggle-btn"
        class:active={$isWorkQueueOpen}
        onclick={toggleWorkQueue}
        title="Open work queue (W)"
        aria-label="Open work queue"
      >
        ☷
      </button>
      <NavTooltip
        title={controlDescriptions.workQueue.title}
        description={controlDescriptions.workQueue.description}
        visible={navTooltipKey === 'workQueue'}
        position="below-left"
      />
    </div>

    <!-- User Profile Dropdown -->
    <ProfileDropdown />
  </div>
</header>

<style>
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.logo) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #1f2937;
    text-decoration: none;
    flex-shrink: 0;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .logo-text {
    display: inline;
  }

  @media (max-width: 1100px) {
    .logo-text {
      display: none;
    }
  }

  /* Time Navigation */
  .time-nav {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .nav-btn {
    padding: 0.25rem 0.375rem;
    font-size: 0.75rem;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .nav-btn:hover {
    color: #1f2937;
    background: #f3f4f6;
  }

  .time-display {
    padding: 0.25rem 0.75rem;
    min-width: 8rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
  }

  .time-display:hover {
    background: #f3f4f6;
  }

  .today-btn {
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #2563eb;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .today-btn:hover {
    background: #eff6ff;
  }

  .nav-tooltip-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* View Buttons */
  .view-buttons {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
  }

  .view-btn-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .view-btn {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.1875rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .view-btn:hover {
    color: #374151;
    background: rgba(255, 255, 255, 0.5);
  }

  .view-btn.active {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .view-btn.promoted {
    border: 1px dashed #d1d5db;
  }

  .view-icon {
    font-size: 0.75rem;
  }

  .view-label {
    font-size: 0.6875rem;
  }

  /* More Dropdown */
  .more-dropdown-container {
    position: relative;
  }

  .more-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    min-width: 10rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 50;
    overflow: visible;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    transition-delay: 0.15s;
  }

  /* Invisible bridge between trigger and dropdown to prevent hover gap */
  .more-dropdown::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .dropdown-item-wrapper {
    position: relative;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: #374151;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
  }

  .dropdown-item:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .dropdown-item.active {
    background: #eff6ff;
    color: #2563eb;
  }

  .dropdown-icon {
    font-size: 0.875rem;
  }

  .dropdown-label {
    flex: 1;
  }

  .dropdown-hint {
    font-size: 0.625rem;
    color: #9ca3af;
  }

  /* Control Dropdowns (Scale, Range) */
  .control-dropdown {
    position: relative;
  }

  .control-trigger {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    height: 1.5rem;
    line-height: 1;
  }

  .control-trigger:hover {
    color: #374151;
    background: #f9fafb;
    border-color: #9ca3af;
  }

  .control-trigger-icon {
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
  }

  .control-trigger-label {
    font-size: 0.6875rem;
  }

  .control-chevron {
    width: 0.5rem;
    height: 0.375rem;
    flex-shrink: 0;
    opacity: 0.5;
  }

  .control-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25rem;
    min-width: 100%;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 50;
    overflow: visible;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
    transition-delay: 0.15s;
  }

  /* Invisible bridge between trigger and dropdown to prevent hover gap */
  .control-dropdown-menu::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .control-dropdown-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    color: #374151;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .control-dropdown-item:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .control-dropdown-item.active {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 600;
  }

  .control-dropdown-item:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .disabled-dropdown {
    opacity: 0.5;
    pointer-events: none;
  }

  /* Hover show for control dropdowns */
  .hover-dropdown:hover > .control-dropdown-menu {
    opacity: 1;
    visibility: visible;
    transition-delay: 0s;
  }

  /* Divider */
  .divider {
    width: 1px;
    height: 1.25rem;
    background: #e5e7eb;
  }

  /* Hover-triggered dropdowns */
  .hover-dropdown:hover > .more-dropdown {
    opacity: 1;
    visibility: visible;
    transition-delay: 0s;
  }

  /* Priority toggle button */
  .priority-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.75rem;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    padding: 0;
    line-height: 1;
  }

  .priority-toggle-btn:hover {
    background: #fff7ed;
    border-color: #fb923c;
  }

  .priority-toggle-btn.active {
    background: #fff7ed;
    border-color: #f97316;
    box-shadow: 0 0 0 1px #f97316;
  }
</style>
